import { zodResolver } from '@hookform/resolvers/zod'
import { ApiError, forgotPasswordSchema } from '@mevaser/shared'
import { Alert, Button, Field, Input } from '@mevaser/ui'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { SignInShell } from '../components/sign-in-shell.js'
import { api } from '../lib/api.js'

type Values = z.infer<typeof forgotPasswordSchema>

/**
 * Asks for a reset link. The confirmation is the same whether or not the
 * address has an account: the API refuses to say, so this page must not
 * either — otherwise it becomes a way to enumerate staff one guess at a time.
 */
export function ForgotPasswordPage() {
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(forgotPasswordSchema) })

  const onSubmit = handleSubmit(async (values) => {
    setError(null)
    try {
      await api.post('/auth/password/forgot', values)
      setSentTo(values.email)
    } catch (err) {
      // A 429 from the route's own per-IP limit is the one thing the caller
      // genuinely needs to see; everything else is already indistinguishable.
      setError(err instanceof ApiError ? err.message : 'Could not send a reset link')
    }
  })

  if (sentTo) {
    return (
      <SignInShell>
        <h2 className="text-base font-semibold tracking-tight">Check your email</h2>
        <p className="mt-2 text-sm text-foreground-muted">
          If <span className="font-medium text-foreground">{sentTo}</span> has a back office
          account, a link to set a new password is on its way. It expires in an hour.
        </p>
        <p className="mt-4 text-center text-xs">
          <Link to="/login" className="text-foreground-muted underline-offset-2 hover:underline">
            Back to sign in
          </Link>
        </p>
      </SignInShell>
    )
  }

  return (
    <SignInShell>
      <h2 className="text-base font-semibold tracking-tight">Reset your password</h2>
      <p className="mt-1 mb-4 text-sm text-foreground-muted">
        We will email you a link to set a new one.
      </p>
      {error ? (
        <Alert tone="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label="Email" error={errors.email?.message} required>
          {(props) => (
            <Input
              {...props}
              {...register('email')}
              type="email"
              autoComplete="username"
              autoFocus
              invalid={!!errors.email}
            />
          )}
        </Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={isSubmitting}>
          Send reset link
        </Button>
      </form>
      <p className="mt-4 text-center text-xs">
        <Link to="/login" className="text-foreground-muted underline-offset-2 hover:underline">
          Back to sign in
        </Link>
      </p>
    </SignInShell>
  )
}
